import React, { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	FormGroup,
	Grid,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
	Chip,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Pagination,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import BackspaceIcon from "@mui/icons-material/Backspace";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link as RouterLink } from "react-router-dom";
import {
	guardarPreparacionExportacionFichajes,
	getPreparacionExportacionFichajes,
} from "../../../services/apis";
import { SubHeader } from "../../../utils/componentes-utiles/SubHeader";
import {
	formatDateTimeEs,
	formatScoreOverHundred,
} from "../../../utils/formatters";


const getTodayYMD = () => {
	const now = new Date();

	return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
		now.getDate()
	).padStart(2, "0")}`;
};

const normalizeTimeValue = (value) => {
	const normalized = String(value ?? "").trim();
	const match = normalized.match(/^(2[0-3]|[01]\d):([0-5]\d)(?::([0-5]\d))?$/);

	if (!match) {
		return "";
	}

	return `${match[1]}:${match[2]}`;
};

const addMinutesToTime = (time, minutesDelta = 0) => {
	const normalizedTime = normalizeTimeValue(time);

	if (!normalizedTime) {
		return "";
	}

	const [hours, minutes] = normalizedTime.split(":").map(Number);
	const totalMinutesInDay = 24 * 60;
	const baseMinutes = hours * 60 + minutes;
	const nextMinutes =
		((baseMinutes + Number(minutesDelta || 0)) % totalMinutesInDay + totalMinutesInDay) %
		totalMinutesInDay;

	return `${String(Math.floor(nextMinutes / 60)).padStart(2, "0")}:${String(
		nextMinutes % 60
	).padStart(2, "0")}`;
};

const generateRandomTimeAroundBase = (time, spreadMinutes = 5) => {
	const normalizedTime = normalizeTimeValue(time);

	if (!normalizedTime) {
		return "";
	}

	const maxOffset = Math.max(0, Math.floor(Number(spreadMinutes) || 0));
	const randomOffset = Math.floor(Math.random() * (maxOffset * 2 + 1)) - maxOffset;

	return addMinutesToTime(normalizedTime, randomOffset);
};

const rowKeyFromItem = (item) => `${item?.id_cuadrilla ?? 0}-${item?.id_usuario ?? 0}`;

const buildHoursMap = (items, fallbackHour, previous = {}) => {
	const next = {};

	items.forEach((item) => {
		const key = rowKeyFromItem(item);

		next[key] =
			normalizeTimeValue(previous[key]) ||
			normalizeTimeValue(item?.hora_entrada_preparada) ||
			fallbackHour;
	});

	return next;
};

const buildSelectionMapFromRows = (items, predicate) => {
	const next = {};

	items.forEach((item) => {
		if (predicate(item)) {
			next[rowKeyFromItem(item)] = true;
		}
	});

	return next;
};

const getEstadoColor = (item) => (Number(item?.exportado ?? 0) === 1 ? "success" : "warning");

export const PrepararExportacionFichajes = () => {
	const today = useMemo(() => getTodayYMD(), []);

	const [fechaDesde, setFechaDesde] = useState(today);
	const [fechaHasta, setFechaHasta] = useState(today);
	const [search, setSearch] = useState("");

	const [filtrosEstado, setFiltrosEstado] = useState({
		validado: true,
		revisado: false,
	});

	const [filtrosPreparacion, setFiltrosPreparacion] = useState({
		pendientes: true,
		preparados: true,
	});

	const [horaBase, setHoraBase] = useState("08:00");
	const [page, setPage] = useState(1);
	const [limit] = useState(25);
	const [rows, setRows] = useState([]);
	const [meta, setMeta] = useState({
		page: 1,
		limit: 25,
		total_pages: 1,
		total_cuadrillas: 0,
	});
	const [hoursByRow, setHoursByRow] = useState({});
	const [horaBaseByGroup, setHoraBaseByGroup] = useState({});
	const [selectedRows, setSelectedRows] = useState({});
	const [resumen, setResumen] = useState({
		total: 0,
		pendientes: 0,
		preparados: 0,
		cuadrillas: 0,
	});
	const [loaded, setLoaded] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const [accessDenied, setAccessDenied] = useState(false);

	const handleFiltroEstadoChange = (campo) => {
		setFiltrosEstado((current) => ({
			...current,
			[campo]: !current[campo],
		}));
	};

	const handleFiltroPreparacionChange = (campo) => {
		setFiltrosPreparacion((current) => ({
			...current,
			[campo]: !current[campo],
		}));
	};

	const visibleRows = useMemo(() => {
		return rows.filter((row) => {
			const preparado = Number(row?.exportado ?? 0) === 1;

			if (preparado && !filtrosPreparacion.preparados) {
				return false;
			}

			if (!preparado && !filtrosPreparacion.pendientes) {
				return false;
			}

			return true;
		});
	}, [rows, filtrosPreparacion]);

	const selectedCount = useMemo(
		() => visibleRows.filter((row) => selectedRows[rowKeyFromItem(row)]).length,
		[visibleRows, selectedRows]
	);

	const groupedRows = useMemo(() => {
		const groups = {};

		visibleRows.forEach((row) => {
			const cuadrillaId = row.id_cuadrilla;

			if (!groups[cuadrillaId]) {
				groups[cuadrillaId] = {
					cuadrilla: {
						id: cuadrillaId,
						nombre: row.nombre_cuadrilla,
						fecha: row.fecha_cuadrilla,
						cliente: row.nombre_cliente,
						cabo: row.cabo_completo,
						nif_cabo: row.nif_cabo,
					},
					fichajes: [],
				};
			}

			groups[cuadrillaId].fichajes.push(row);
		});

		return Object.values(groups);
	}, [visibleRows]);

	const allVisibleSelected = useMemo(() => {
		if (visibleRows.length === 0) return false;

		return visibleRows.every((row) => selectedRows[rowKeyFromItem(row)]);
	}, [visibleRows, selectedRows]);

	const resumenVisible = useMemo(() => {
		const total = visibleRows.length;
		const preparados = visibleRows.filter((item) => Number(item?.exportado ?? 0) === 1).length;
		const pendientes = total - preparados;
		const cuadrillas = new Set(visibleRows.map((item) => item.id_cuadrilla)).size;

		return {
			total,
			pendientes,
			preparados,
			cuadrillas,
		};
	}, [visibleRows]);

	const criterioEstado = useMemo(() => {
		const validado = filtrosEstado.validado === true;
		const revisado = filtrosEstado.revisado === true;

		// Ambos activos = exigir Validado + Revisado
		if (validado && revisado) return "ambas";
		if (validado) return "validado_solo";
		if (revisado) return "revisado_solo";

		// Ninguno activo = sin filtro (mejor UX que dejarlo vacío)
		return "todos";
	}, [filtrosEstado.validado, filtrosEstado.revisado]);

	const cargarDatos = async ({ preserveMessage = false, nextPage = page } = {}) => {
		setLoaded(false);
		setError("");

		if (!preserveMessage) {
			setMessage("");
		}

		setAccessDenied(false);

		if (!fechaDesde || !fechaHasta) {
			setRows([]);
			setResumen({ total: 0, pendientes: 0, preparados: 0, cuadrillas: 0 });
			setError("Debes indicar ambas fechas.");
			setLoaded(true);
			return;
		}

		if (fechaDesde > fechaHasta) {
			setRows([]);
			setResumen({ total: 0, pendientes: 0, preparados: 0, cuadrillas: 0 });
			setError("La fecha desde no puede ser mayor que la fecha hasta.");
			setLoaded(true);
			return;
		}

		try {
			const response = await getPreparacionExportacionFichajes({
				fechaDesde,
				fechaHasta,
				soloPendientes: false,
				search,
				criterioEstado,
				validado: filtrosEstado.validado,
				revisado: filtrosEstado.revisado,
				page: nextPage,
				limit,
			});

			if (response?.success) {
				const data = Array.isArray(response.data) ? response.data : [];
				const resumenResponse = response.resumen || {};
				const metaResponse = response.meta || {};
				setRows(data);
				setMeta({
					page: Number(metaResponse.page ?? nextPage) || 1,
					limit: Number(metaResponse.limit ?? limit) || limit,
					total_pages: Number(metaResponse.total_pages ?? 1) || 1,
					total_cuadrillas: Number(metaResponse.total_cuadrillas ?? 0) || 0,
				});
				setPage(Number(metaResponse.page ?? nextPage) || nextPage || 1);
				setResumen({
					total: Number(resumenResponse.total ?? 0),
					pendientes: Number(resumenResponse.pendientes ?? 0),
					preparados: Number(resumenResponse.preparados ?? 0),
					cuadrillas: Number(resumenResponse.cuadrillas ?? 0),
				});

				setHoursByRow((current) => buildHoursMap(data, horaBase, current));

				setSelectedRows((current) => {
					const next = {};

					data.forEach((item) => {
						const key = rowKeyFromItem(item);

						if (current[key]) {
							next[key] = true;
						}
					});

					return next;
				});
			} else {
				setRows([]);
				setMeta({ page: 1, limit, total_pages: 1, total_cuadrillas: 0 });
				setResumen({ total: 0, pendientes: 0, preparados: 0, cuadrillas: 0 });
				setError(response?.message || "No se pudo cargar la preparacion.");
			}
		} catch (err) {
			const status = err?.response?.status;

			if (status === 403) {
				setAccessDenied(true);
				setRows([]);
				setMeta({ page: 1, limit, total_pages: 1, total_cuadrillas: 0 });
				setResumen({ total: 0, pendientes: 0, preparados: 0, cuadrillas: 0 });
				setError("Solo los roles 986 y 901 pueden acceder a este panel.");
			} else {
				console.error("Error cargando preparacion de exportacion:", err);
				setRows([]);
				setMeta({ page: 1, limit, total_pages: 1, total_cuadrillas: 0 });
				setResumen({ total: 0, pendientes: 0, preparados: 0, cuadrillas: 0 });
				setError("No se pudo cargar la preparacion de exportacion.");
			}
		} finally {
			setLoaded(true);
		}
	};

	useEffect(() => {
		cargarDatos();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const toggleRow = (item) => {
		const key = rowKeyFromItem(item);

		setSelectedRows((current) => ({
			...current,
			[key]: !current[key],
		}));
	};

	const toggleAllVisible = () => {
		setSelectedRows((current) => {
			const next = { ...current };

			visibleRows.forEach((item) => {
				const key = rowKeyFromItem(item);
				next[key] = !allVisibleSelected;
			});

			return next;
		});
	};

	const seleccionarPendientes = () => {
		setSelectedRows(
			buildSelectionMapFromRows(
				visibleRows,
				(item) => Number(item?.exportado ?? 0) !== 1
			)
		);
	};

	const limpiarSeleccion = () => {
		setSelectedRows({});
	};

	const toggleAllInGroup = (group) => {
		const allInGroupSelected = group.fichajes.every(
			(item) => selectedRows[rowKeyFromItem(item)]
		);

		setSelectedRows((current) => {
			const next = { ...current };

			group.fichajes.forEach((item) => {
				const key = rowKeyFromItem(item);
				next[key] = !allInGroupSelected;
			});

			return next;
		});
	};

	const applyHourBaseToGroup = (group) => {
		const cuadrillaId = group.cuadrilla.id;
		const baseHour = normalizeTimeValue(horaBaseByGroup[cuadrillaId] || horaBase);

		if (!baseHour) {
			setError("La hora base para esta cuadrilla no es valida.");
			return;
		}

		setError("");

		setHoursByRow((current) => {
			const nextEntrada = { ...current };

			group.fichajes.forEach((item) => {
				const key = rowKeyFromItem(item);
				nextEntrada[key] = generateRandomTimeAroundBase(baseHour, 5);
			});

			return nextEntrada;
		});
	};

	const aplicarHoraBase = () => {
		if (!selectedCount) {
			setError("Selecciona al menos un fichaje para aplicar la hora.");
			return;
		}

		const normalizedBaseHour = normalizeTimeValue(horaBase);

		if (!normalizedBaseHour) {
			setError("La hora base no es valida.");
			return;
		}

		setError("");

		setHoursByRow((current) => {
			const nextEntrada = { ...current };

			visibleRows.forEach((item) => {
				const key = rowKeyFromItem(item);

				if (selectedRows[key]) {
					nextEntrada[key] = generateRandomTimeAroundBase(normalizedBaseHour, 5);
				}
			});

			return nextEntrada;
		});
	};

	const handleHoraEntradaChange = (item, value) => {
		const key = rowKeyFromItem(item);
		const entrada = normalizeTimeValue(value);

		setHoursByRow((current) => ({
			...current,
			[key]: entrada,
		}));
	};

	const guardarSeleccion = async (action) => {
		const selectedItems = visibleRows.filter((item) => selectedRows[rowKeyFromItem(item)]);

		if (selectedItems.length === 0) {
			setError("Selecciona al menos un fichaje.");
			return;
		}

		const payloadItems =
			action === "prepare"
				? selectedItems.map((item) => {
						const key = rowKeyFromItem(item);
						// Este panel envia solo la hora de entrada; la logica de convenios/salida queda fuera.
						const horaEntrada =
							normalizeTimeValue(hoursByRow[key]) ||
							normalizeTimeValue(item?.hora_entrada_preparada) ||
							normalizeTimeValue(horaBase);

						return {
							id_cuadrilla: item.id_cuadrilla,
							id_usuario: item.id_usuario,
							hora_entrada: horaEntrada,
						};
				  })
				: selectedItems.map((item) => ({
						id_cuadrilla: item.id_cuadrilla,
						id_usuario: item.id_usuario,
				  }));

		if (
			action === "prepare" &&
			payloadItems.some((item) => !normalizeTimeValue(item.hora_entrada))
		) {
			setError("Hay fichajes seleccionados sin una hora de entrada valida.");
			return;
		}

		setSaving(true);
		setError("");
		setMessage("");

		try {
			const response = await guardarPreparacionExportacionFichajes({
				action,
				items: payloadItems,
			});

			if (response?.success) {
				await cargarDatos({ preserveMessage: true });
				setMessage(response.message || "Preparacion actualizada.");
			} else {
				setError(response?.message || "No se pudo guardar la preparacion.");
			}
		} catch (err) {
			console.error("Error guardando preparacion:", err);
			setError("No se pudo guardar la preparacion de exportacion.");
		} finally {
			setSaving(false);
		}
	};

	return (
		<Grid container direction="column" size={{ xs: 12 }}>
			<Grid item size={{ xs: 12 }}>
				<SubHeader titulo="Preparar exportacion de fichajes" />
			</Grid>

			<Grid container size={{ xs: 12 }} sx={{ px: 2, pb: 2 }}>
				<Button
					variant="text"
					startIcon={<ArrowBackIcon />}
					component={RouterLink}
					to="/admin"
				>
					Volver a administracion
				</Button>
			</Grid>

			<Grid container spacing={2} sx={{ px: 2, pb: 2 }}>
				<Grid item size={{ xs: 12, md: 2 }}>
					<TextField
						label="Fecha desde"
						type="date"
						size="small"
						fullWidth
						value={fechaDesde}
						onChange={(event) => setFechaDesde(event.target.value)}
						InputLabelProps={{ shrink: true }}
					/>
				</Grid>

				<Grid item size={{ xs: 12, md: 2 }}>
					<TextField
						label="Fecha hasta"
						type="date"
						size="small"
						fullWidth
						value={fechaHasta}
						onChange={(event) => setFechaHasta(event.target.value)}
						InputLabelProps={{ shrink: true }}
					/>
				</Grid>

				<Grid item size={{ xs: 12, md: 2 }}>
					<TextField
						label="Hora base"
						type="time"
						size="small"
						fullWidth
						value={horaBase}
						onChange={(event) => setHoraBase(normalizeTimeValue(event.target.value))}
						InputLabelProps={{ shrink: true }}
						inputProps={{ step: 60 }}
					/>
				</Grid>

				<Grid item size={{ xs: 12, md: 3 }}>
					<TextField
						label="Buscar"
						size="small"
						fullWidth
						value={search}
						onChange={(event) => setSearch(event.target.value)}
						placeholder="Persona, NIF, cliente, cabo o cuadrilla"
					/>
				</Grid>

				<Grid item size={{ xs: 12, md: 3 }}>
					<Box
						sx={{
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 1,
							px: 1.5,
							py: 0.75,
						}}
					>
						<Typography variant="caption" color="text.secondary">
							Filtros de estado
						</Typography>

						<FormGroup row>
							<FormControlLabel
								control={
									<Checkbox
										size="small"
										checked={filtrosEstado.validado}
										onChange={() => handleFiltroEstadoChange("validado")}
									/>
								}
								label="Validado"
							/>

							<FormControlLabel
								control={
									<Checkbox
										size="small"
										checked={filtrosEstado.revisado}
										onChange={() => handleFiltroEstadoChange("revisado")}
									/>
								}
								label="Revisado"
							/>
						</FormGroup>
					</Box>
				</Grid>

				<Grid item size={{ xs: 12, md: 3 }}>
					<Box
						sx={{
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 1,
							px: 1.5,
							py: 0.75,
						}}
					>
						<Typography variant="caption" color="text.secondary">
							Filtro de preparación
						</Typography>

						<FormGroup row>
							<FormControlLabel
								control={
									<Checkbox
										size="small"
										checked={filtrosPreparacion.pendientes}
										onChange={() => handleFiltroPreparacionChange("pendientes")}
									/>
								}
								label="Pendientes"
							/>

							<FormControlLabel
								control={
									<Checkbox
										size="small"
										checked={filtrosPreparacion.preparados}
										onChange={() => handleFiltroPreparacionChange("preparados")}
									/>
								}
								label="Exportados"
							/>
						</FormGroup>
					</Box>
				</Grid>

				<Grid
					item
					size={{ xs: 12, md: 5 }}
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						flexWrap: "wrap",
					}}
				>
					<Button
						variant="contained"
						onClick={() => {
							setPage(1);
							cargarDatos({ nextPage: 1 });
						}}
						disabled={saving}
						startIcon={<RefreshIcon />}
					>
						Buscar
					</Button>

					<Typography variant="caption" color="text.secondary">
						Validado y Revisado filtran exactamente su propio campo
					</Typography>
				</Grid>
			</Grid>

			<Grid container spacing={2} sx={{ px: 2, pb: 2 }}>
				<Grid item size={{ xs: 12, sm: 6, md: 3 }}>
					<Paper variant="outlined" sx={{ p: 2 }}>
						<Typography variant="caption" color="text.secondary">
							Total visible
						</Typography>
						<Typography variant="h5">{resumenVisible.total}</Typography>
						<Typography variant="caption" color="text.secondary">
							Total cargado: {resumen.total}
						</Typography>
					</Paper>
				</Grid>

				<Grid item size={{ xs: 12, sm: 6, md: 3 }}>
					<Paper variant="outlined" sx={{ p: 2 }}>
						<Typography variant="caption" color="text.secondary">
							Pendientes visibles
						</Typography>
						<Typography variant="h5">{resumenVisible.pendientes}</Typography>
						<Typography variant="caption" color="text.secondary">
							Pendientes cargados: {resumen.pendientes}
						</Typography>
					</Paper>
				</Grid>

				<Grid item size={{ xs: 12, sm: 6, md: 3 }}>
					<Paper variant="outlined" sx={{ p: 2 }}>
						<Typography variant="caption" color="text.secondary">
							Exportados visibles
						</Typography>

						<Typography variant="h5">
							{resumenVisible.preparados}/{resumenVisible.total}
						</Typography>

						<Typography variant="caption" color="text.secondary">
							{resumenVisible.total > 0
								? `${Math.round(
										(resumenVisible.preparados / resumenVisible.total) * 100
								  )}% exportado`
								: "0% exportado"}
						</Typography>
					</Paper>
				</Grid>

				<Grid item size={{ xs: 12, sm: 6, md: 3 }}>
					<Paper variant="outlined" sx={{ p: 2 }}>
						<Typography variant="caption" color="text.secondary">
							Cuadrillas visibles
						</Typography>
						<Typography variant="h5">{resumenVisible.cuadrillas}</Typography>
						<Typography variant="caption" color="text.secondary">
							Cuadrillas cargadas: {resumen.cuadrillas}/{meta.total_cuadrillas} (página {meta.page}/{meta.total_pages})
						</Typography>
					</Paper>
				</Grid>
			</Grid>

			{meta.total_pages > 1 && (
				<Grid container sx={{ px: 2, pb: 2 }} justifyContent="center">
					<Pagination
						count={meta.total_pages}
						page={page}
						onChange={(_, value) => {
							setPage(value);
							cargarDatos({ preserveMessage: true, nextPage: value });
							window.scrollTo({ top: 0, behavior: "smooth" });
						}}
						color="primary"
					/>
				</Grid>
			)}

			<Grid container spacing={1} sx={{ px: 2, pb: 2 }} alignItems="center">
				<Button
					variant="outlined"
					size="small"
					onClick={toggleAllVisible}
					disabled={!visibleRows.length || saving}
					startIcon={<DoneAllIcon />}
				>
					{allVisibleSelected ? "Deseleccionar visibles" : "Seleccionar visibles"}
				</Button>

				<Button
					variant="outlined"
					size="small"
					onClick={seleccionarPendientes}
					disabled={!visibleRows.length || saving}
					startIcon={<PlaylistAddCheckIcon />}
				>
					Seleccionar pendientes
				</Button>

				<Button
					variant="outlined"
					size="small"
					onClick={limpiarSeleccion}
					disabled={!selectedCount || saving}
					startIcon={<BackspaceIcon />}
				>
					Limpiar seleccion
				</Button>

				<Button
					variant="outlined"
					size="small"
					onClick={aplicarHoraBase}
					disabled={!selectedCount || saving}
					startIcon={<ScheduleIcon />}
				>
					Aplicar hora base
				</Button>

				<Button
					variant="contained"
					size="small"
					onClick={() => guardarSeleccion("prepare")}
					disabled={!selectedCount || saving || accessDenied}
					startIcon={<FileUploadIcon />}
				>
					EXPORTAR SELECCIONADOS
				</Button>

				<Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
					Seleccionados visibles: {selectedCount}
				</Typography>
			</Grid>

			{!!message && (
				<Grid sx={{ px: 2, pb: 2 }}>
					<Alert severity="success">{message}</Alert>
				</Grid>
			)}

			{!!error && (
				<Grid sx={{ px: 2, pb: 2 }}>
					<Alert severity={accessDenied ? "warning" : "error"}>{error}</Alert>
				</Grid>
			)}

			<Grid sx={{ px: 2, pb: 3 }}>
				<Paper variant="outlined">
					{!loaded ? (
						<Box
							sx={{
								minHeight: "40vh",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 1,
							}}
						>
							<CircularProgress size={20} />
							<Typography variant="body2">Cargando fichajes...</Typography>
						</Box>
					) : visibleRows.length === 0 ? (
						<Box sx={{ p: 3 }}>
							<Alert severity="info">
								No hay fichajes para el rango y filtro seleccionados.
							</Alert>
						</Box>
					) : (
						<Box>
							{groupedRows.map((group) => {
								const cuadrillaId = group.cuadrilla.id;
								const allInGroupSelected = group.fichajes.every(
									(item) => selectedRows[rowKeyFromItem(item)]
								);
								const totalFichajes = group.fichajes.length;
								const preparados = group.fichajes.filter(
									(item) => Number(item?.exportado ?? 0) === 1
								).length;
								const pendientes = totalFichajes - preparados;

								return (
									<Accordion key={cuadrillaId}>
										<AccordionSummary expandIcon={<ExpandMoreIcon />}>
											<Box sx={{ width: "100%" }}>
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 1,
														mb: 1,
													}}
												>
													<Typography variant="h6" sx={{ fontWeight: 600 }}>
														{group.cuadrilla.nombre || `Cuadrilla ${cuadrillaId}`}
													</Typography>
												</Box>

												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 2,
														width: "100%",
														flexWrap: "wrap",
													}}
												>
													<Button
														size="small"
														variant="outlined"
														onClick={(e) => {
															e.stopPropagation();
															toggleAllInGroup(group);
														}}
														disabled={saving}
													>
														{allInGroupSelected
															? "Deseleccionar todos"
															: "Seleccionar todos"}
													</Button>

													<Chip
														label={`${totalFichajes} fichaje${
															totalFichajes !== 1 ? "s" : ""
														}`}
														size="small"
														color="primary"
														variant="outlined"
													/>

													<Chip
														label={`${preparados}/${totalFichajes} exportados`}
														size="small"
														color={
															preparados === totalFichajes
																? "success"
																: preparados > 0
																? "warning"
																: "default"
														}
														variant={
															preparados === totalFichajes ? "filled" : "outlined"
														}
													/>

													<Typography variant="body2" color="text.secondary">
														Pendientes: {pendientes}
													</Typography>

													<Typography variant="body2" color="text.secondary">
														Cliente: {group.cuadrilla.cliente || "-"}
													</Typography>

								<Typography variant="body2" color="text.secondary">
									Fecha:{" "}
									{formatDateTimeEs(group.cuadrilla.fecha, {
										includeTime: false,
									})}
								</Typography>

								<Box sx={{ flex: 1, minWidth: 120 }} />

													<Box
														sx={{
															display: "flex",
															gap: 1,
															alignItems: "center",
														}}
														onClick={(e) => e.stopPropagation()}
													>
														<TextField
															label="Hora base"
															type="time"
															size="small"
															value={normalizeTimeValue(
																horaBaseByGroup[cuadrillaId] || horaBase
															)}
															onChange={(event) =>
																setHoraBaseByGroup((current) => ({
																	...current,
																	[cuadrillaId]: normalizeTimeValue(
																		event.target.value
																	),
																}))
															}
															InputLabelProps={{ shrink: true }}
															inputProps={{ step: 60 }}
															disabled={saving}
															sx={{ width: 140 }}
														/>

														<Button
															size="small"
															variant="outlined"
															onClick={(e) => {
																e.stopPropagation();
																applyHourBaseToGroup(group);
															}}
															disabled={saving}
														>
															Aplicar
														</Button>
													</Box>
												</Box>
											</Box>
										</AccordionSummary>

										<AccordionDetails>
											<Table sx={{ tableLayout: "fixed" }}>
											<TableHead>
												<TableRow>
													<TableCell sx={{ width: "60px" }}>
														Seleccionar
													</TableCell>
													<TableCell sx={{ width: "150px" }}>Usuario</TableCell>
													<TableCell sx={{ width: "130px" }}>
														Hora Entrada
													</TableCell>
													<TableCell sx={{ width: "100px" }}>NIF</TableCell>
													<TableCell sx={{ width: "80px" }}>Empleo</TableCell>
													<TableCell sx={{ width: "120px" }}>Similitud</TableCell>
														<TableCell sx={{ width: "110px" }}>Estado</TableCell>
														<TableCell sx={{ width: "140px" }}>
															Fecha Exportado
														</TableCell>
													</TableRow>
												</TableHead>

												<TableBody>
													{group.fichajes.map((item) => {
														const key = rowKeyFromItem(item);
														const prepared = Number(item?.exportado ?? 0) === 1;

														const horaEntrada =
															normalizeTimeValue(hoursByRow[key]) ||
															normalizeTimeValue(item?.hora_entrada_preparada) ||
															horaBase;

														return (
															<TableRow
																key={key}
																onClick={() => toggleRow(item)}
																sx={{ cursor: "pointer" }}
															>
																<TableCell>
																	<Checkbox
																		checked={Boolean(selectedRows[key])}
																		onChange={(e) => {
																			e.stopPropagation();
																			toggleRow(item);
																		}}
																		size="small"
																	/>
																</TableCell>

																<TableCell>
																	<Box
																		sx={{
																			overflow: "hidden",
																			textOverflow: "ellipsis",
																			whiteSpace: "nowrap",
																		}}
																	>
																		{item.nombre_completo || "-"}
																	</Box>
																</TableCell>

																<TableCell>
																	<TextField
																		label="Hora entrada"
																		type="time"
																		size="small"
																		value={horaEntrada}
																		onChange={(event) =>
																			handleHoraEntradaChange(
																				item,
																				event.target.value
																			)
																		}
																		InputLabelProps={{ shrink: true }}
																		inputProps={{ step: 60 }}
																		disabled={saving}
																		onClick={(e) => e.stopPropagation()}
																	/>
																</TableCell>

																<TableCell>{item.nif_recolector || "-"}</TableCell>
																<TableCell>{item.id_empleo || "-"}</TableCell>

																<TableCell>
																	{item?.puntuacion_comprobacion !== undefined &&
																	item?.puntuacion_comprobacion !== null ? (
																		<Chip
																			size="small"
																			variant="outlined"
																			label={`Similitud: ${formatScoreOverHundred(
																				item.puntuacion_comprobacion
																			)}`}
																		/>
																	) : (
																		"-"
																	)}
																</TableCell>

																<TableCell>
																	<Chip
																		size="small"
																		color={getEstadoColor(item)}
																		label={prepared ? "Exportado" : "Pendiente"}
																	/>
																</TableCell>

																<TableCell>
																	{item.fecha_exportado_in
																		? formatDateTimeEs(item.fecha_exportado_in)
																		: "-"}
																</TableCell>
															</TableRow>
														);
													})}
												</TableBody>
											</Table>
										</AccordionDetails>
									</Accordion>
								);
							})}
						</Box>
					)}
				</Paper>
			</Grid>

		</Grid>
	);
};
